import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { apiService } from '../services/api.service';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Notification {
  id: string;
  type: 'registration_received' | 'heat_announced' | 'heat_schedule_changed' | 'results_available' | 'new_account';
  title: string;
  message?: string;
  related_event_id?: string;
  related_data?: Record<string, any>;
  is_read: boolean;
  action_url?: string;
  created_at: string;
  read_at?: string;
}


const POLLING_INTERVAL = 30000; // 30 secondes

export function NotificationBell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Récupérer le compteur non-lues
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.getNotificationsUnreadCount();
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Erreur récupération compteur notifications:', error);
    }
  }, []);

  // Récupérer les notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getNotifications(5, 0, false);
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId: string) => {
    try {
      await apiService.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  // Marquer toutes comme lues
  const markAllAsRead = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await apiService.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur marquage toutes notifications:', error);
    }
  };

  // Clic sur une notification
  // Le marquage est ATTENDU avant de partir : un `window.location.href`
  // immédiat rechargeait la page et annulait la requête en vol — la
  // notification restait non lue (constaté le 2026-09-04). Une URL du même
  // site se suit par le routeur, sans rechargement.
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    if (!notification.action_url) return;
    const url = new URL(notification.action_url, window.location.origin);
    if (url.origin === window.location.origin) {
      navigate(url.pathname + url.search + url.hash);
    } else {
      window.location.href = notification.action_url;
    }
  };

  // Polling pour le compteur
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Charger les notifications quand le menu s'ouvre
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={t('notifications.title')}
              className="relative p-2 hover:bg-muted rounded-md transition-colors"
            >
              <Bell size={20} className="text-foreground" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 glass-ring [--ring-w:1px] flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tabular-nums text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent><p className="text-xs">{t('notifications.title')}</p></TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-80">
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-2">
          <DropdownMenuLabel className="py-0">Notifications</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={markAllAsRead}
            >
              <CheckCheck size={14} className="mr-1" />
              Tout lire
            </Button>
          )}
        </div>

        <DropdownMenuSeparator />

        {/* Liste des notifications */}
        <div className="max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-20">
              <span className="text-sm text-muted-foreground">Chargement...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-20 text-center px-4">
              <Bell size={24} className="text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">Aucune notification</span>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`px-3 py-3 cursor-pointer ${
                    !notification.is_read ? 'bg-white/[0.05]' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${!notification.is_read ? 'font-medium' : ''}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </div>
                      {notification.message && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: fr
                        })}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-xs cursor-pointer"
          onClick={() => {
            navigate('/admin/notifications');
            setIsOpen(false);
          }}
        >
          Voir toutes les notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationBell;
